import { useState } from "react";
import { Link, useParams, useLocation } from "wouter";
import { useContextoDaTela } from "@/features/assistente/contexto-de-tela";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import ContactTimeline from "@/components/ContactTimeline";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import ContactSearchCombobox from "@/components/Crm/ContactSearchCombobox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ArrowLeft,
  Briefcase,
  Loader2,
  Plus,
  Trash2,
  Users,
  Clock,
  Phone,
  Mail,
  MessageCircle,
  FileText,
  CheckCircle2,
  XCircle,
  Calendar,
  ChevronRight,
  Crown,
  ExternalLink,
  Check,
  Banknote,
  Target,
  Activity,
  Package,
  MessageSquare,
  UserPlus,
  UserMinus,
  UserCog,
  ArrowRightLeft,
  CalendarClock,
  FileSignature,
} from "lucide-react";
import DealProductsPanel from "@/components/Crm/DealProductsPanel";
import DealProposalsPanel from "@/components/Crm/DealProposalsPanel";
import DealTeamPanel from "@/components/Crm/DealTeamPanel";
import TransferDealDialog, { DealTeamRoleBadge } from "@/components/Crm/TransferDealDialog";
import MeetingsCard from "@/components/Agenda/MeetingsCard";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import ConfirmLossDialog from "@/components/Crm/ConfirmLossDialog";
import InlineEditText from "@/components/inline-edit/InlineEditText";
import InlineEditCurrency from "@/components/inline-edit/InlineEditCurrency";
import InlineEditDate from "@/components/inline-edit/InlineEditDate";
import InlineEditSelect from "@/components/inline-edit/InlineEditSelect";
import InlineEditContact from "@/components/inline-edit/InlineEditContact";
import { useDealFieldEdit } from "@/hooks/useDealFieldEdit";
import { useDealSources } from "@/lib/crm-source";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDateTime(d: Date | string | null | undefined) {
  if (!d) return "-";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function formatRelative(d: Date | string | null | undefined) {
  if (!d) return "-";
  const date = typeof d === "string" ? new Date(d) : d;
  const diffMs = Date.now() - date.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min}min atrás`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h atrás`;
  const dias = Math.round(h / 24);
  if (dias < 30) return `${dias}d atrás`;
  return date.toLocaleDateString("pt-BR");
}

function initials(name: string | null | undefined, fb = "?"): string {
  if (!name) return fb;
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const PARTICIPANT_ROLES: Record<string, { label: string; color: string; icon: any }> = {
  decisor: {
    label: "Decisor",
    color: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
    icon: Crown,
  },
  influenciador: {
    label: "Influenciador",
    color: "bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-300",
    icon: Users,
  },
  tecnico: {
    label: "Técnico",
    color: "bg-purple-100 text-purple-900 dark:bg-purple-950 dark:text-purple-300",
    icon: Activity,
  },
  financeiro: {
    label: "Financeiro",
    color: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300",
    icon: Banknote,
  },
  usuario_final: {
    label: "Usuário final",
    color: "bg-slate-100 text-slate-900 dark:bg-slate-900 dark:text-slate-300",
    icon: Target,
  },
};

const ACTIVITY_CFG: Record<string, { icon: any; label: string; tint: string }> = {
  note: { icon: FileText, label: "Nota", tint: "bg-muted text-muted-foreground" },
  call: { icon: Phone, label: "Ligação", tint: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" },
  meeting: {
    icon: Users,
    label: "Reunião",
    tint: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
  },
  proposal: {
    icon: FileText,
    label: "Proposta",
    tint: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300",
  },
  email: { icon: Mail, label: "E-mail", tint: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300" },
  whatsapp: {
    icon: MessageCircle,
    label: "WhatsApp",
    tint: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  },
  stage_changed: {
    icon: ChevronRight,
    label: "Movida de estágio",
    tint: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  },
  deal_created: { icon: Plus, label: "Criada", tint: "bg-primary/10 text-primary" },
  deal_won: {
    icon: CheckCircle2,
    label: "Fechada",
    tint: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  },
  deal_lost: {
    icon: XCircle,
    label: "Perdida",
    tint: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
  },
  participant_added: {
    icon: Users,
    label: "Participante",
    tint: "bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300",
  },
  owner_changed: {
    icon: Users,
    label: "Responsável",
    tint: "bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300",
  },
  team_member_added: {
    icon: UserPlus,
    label: "Time",
    tint: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  },
  team_member_removed: {
    icon: UserMinus,
    label: "Time",
    tint: "bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300",
  },
  deal_transferred: {
    icon: ArrowRightLeft,
    label: "Transferida",
    tint: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
  },
  task_created: { icon: Clock, label: "Tarefa criada", tint: "bg-muted text-muted-foreground" },
  task_completed: {
    icon: CheckCircle2,
    label: "Tarefa concluída",
    tint: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  },
  system: { icon: Activity, label: "Sistema", tint: "bg-muted text-muted-foreground" },
};

// ─── Pipeline tracker (stages como stepper) ──────────────────────────────────
function PipelineTracker({
  stages,
  currentStageId,
  status,
  canChange,
  onMove,
}: {
  stages: any[];
  currentStageId: number;
  status: string;
  canChange: boolean;
  onMove: (stageId: number, isLost: boolean) => void;
}) {
  const normalStages = stages.filter((s) => !s.isWon && !s.isLost);
  const wonStage = stages.find((s) => s.isWon);
  const lostStage = stages.find((s) => s.isLost);

  const currentIdx = normalStages.findIndex((s) => s.id === currentStageId);
  const isWon = status === "won";
  const isLost = status === "lost";

  return (
    <div className="space-y-3">
      <div className="-mx-1 px-1 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max md:min-w-0">
          {normalStages.map((s, idx) => {
            const passed = currentIdx >= idx && !isLost;
            const current = s.id === currentStageId && !isWon && !isLost;
            const disabled = !canChange || isLost || isWon;
            return (
              <div key={s.id} className="flex-1 min-w-[110px] md:min-w-0 flex items-center gap-1">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onMove(s.id, false)}
                  className={`group flex-1 min-w-0 h-9 rounded-md px-3 text-xs font-medium transition relative overflow-hidden ${
                    current
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : passed
                      ? "bg-primary/10 text-primary hover:bg-primary/20"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  } ${disabled ? "cursor-default opacity-70" : "cursor-pointer"}`}
                  title={`${s.name} (${s.probability}%)`}
                >
                  <span className="block truncate">{s.name}</span>
                </button>
                {idx < normalStages.length - 1 && (
                  <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      </div>
      {canChange && !isWon && !isLost && (
        <div className="flex flex-col sm:flex-row gap-2">
          {wonStage && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-900 dark:hover:bg-emerald-950"
              onClick={() => onMove(wonStage.id, false)}
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
              Marcar como fechada
            </Button>
          )}
          {lostStage && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-red-700 border-red-300 hover:bg-red-50 dark:text-red-300 dark:border-red-900 dark:hover:bg-red-950"
              onClick={() => onMove(lostStage.id, true)}
            >
              <XCircle className="h-4 w-4 mr-1.5" />
              Marcar como perdida
            </Button>
          )}
        </div>
      )}
      {isWon && wonStage && (
        <div className="flex items-center justify-center gap-2 rounded-lg py-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-300">
          <CheckCircle2 className="h-5 w-5" />
          <span className="font-semibold">Oportunidade fechada!</span>
        </div>
      )}
      {isLost && lostStage && (
        <div className="flex items-center justify-center gap-2 rounded-lg py-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300">
          <XCircle className="h-5 w-5" />
          <span className="font-semibold">Oportunidade perdida</span>
        </div>
      )}
    </div>
  );
}

// ─── Timeline ────────────────────────────────────────────────────────────────
function ActivityTimeline({ dealId }: { dealId: number }) {
  const activitiesQuery = trpc.crm.deals.listActivities.useQuery({ dealId });
  const utils = trpc.useUtils();
  const [content, setContent] = useState("");
  const [type, setType] = useState<"note" | "call" | "meeting" | "proposal" | "email" | "whatsapp">(
    "note",
  );
  const addNote = trpc.crm.deals.addNote.useMutation({
    onSuccess: () => {
      setContent("");
      utils.crm.deals.listActivities.invalidate({ dealId });
      toast.success("Registrado");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const quickTypes: Array<{ key: typeof type; label: string; icon: any }> = [
    { key: "note", label: "Nota", icon: FileText },
    { key: "call", label: "Ligação", icon: Phone },
    { key: "meeting", label: "Reunião", icon: Users },
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { key: "email", label: "E-mail", icon: Mail },
    { key: "proposal", label: "Proposta", icon: FileText },
  ];

  return (
    <div className="space-y-4">
      {/* Quick add */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-1 mb-2">
            {quickTypes.map((t) => {
              const Icon = t.icon;
              const active = type === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setType(t.key)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                    active
                      ? "bg-primary text-primary-foreground border-transparent"
                      : "bg-background hover:bg-muted"
                  }`}
                >
                  <Icon className="h-3 w-3 inline mr-1" />
                  {t.label}
                </button>
              );
            })}
          </div>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="O que aconteceu? (Ctrl+Enter para enviar)"
            rows={2}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && content.trim()) {
                addNote.mutate({ dealId, type, content: content.trim() });
              }
            }}
          />
          <div className="flex justify-end mt-2">
            <Button
              size="sm"
              onClick={() => {
                if (!content.trim()) return;
                addNote.mutate({ dealId, type, content: content.trim() });
              }}
              disabled={addNote.isPending || !content.trim()}
            >
              {addNote.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Registrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Events */}
      <div className="relative">
        {(activitiesQuery.data ?? []).length > 0 && (
          <div className="absolute left-[19px] top-0 bottom-0 w-px bg-border" />
        )}
        <div className="space-y-4">
          {(activitiesQuery.data ?? []).map((item: any) => {
            const cfg = ACTIVITY_CFG[item.activity.type] ?? ACTIVITY_CFG.note;
            const Icon = cfg.icon;
            return (
              <div key={item.activity.id} className="relative flex gap-3">
                <div
                  className={`h-10 w-10 rounded-full flex items-center justify-center ring-4 ring-background shrink-0 ${cfg.tint}`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0 pt-1">
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="font-medium">{item.user?.name ?? "Sistema"}</span>
                    <Badge variant="secondary" className="text-[10px] h-5">
                      {cfg.label}
                    </Badge>
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-xs text-muted-foreground cursor-help">
                            {formatRelative(item.activity.createdAt)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {formatDateTime(item.activity.createdAt)}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {item.activity.content && (
                    <div className="mt-1 text-sm whitespace-pre-wrap break-words">
                      {item.activity.content}
                    </div>
                  )}
                  {(item.activity.type === "deal_transferred" ||
                    item.activity.type === "owner_changed") &&
                    item.activity.metadata?.reason && (
                      <div className="mt-1 text-xs text-muted-foreground break-words">
                        Motivo: {item.activity.metadata.reason}
                      </div>
                    )}
                </div>
              </div>
            );
          })}
          {(activitiesQuery.data ?? []).length === 0 && (
            <div className="text-center py-10 text-muted-foreground">
              <Activity className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Registre a primeira interação com este contato.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Participants ────────────────────────────────────────────────────────────
function ParticipantsPanel({ dealId }: { dealId: number }) {
  const listQuery = trpc.crm.deals.listParticipants.useQuery({ dealId });
  // Carrega o deal só para obter o contato principal e herdar dados.
  const dealQuery = trpc.crm.deals.get.useQuery({ id: dealId });
  const mainContact = dealQuery.data?.contact ?? null;
  const utils = trpc.useUtils();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [contact, setContact] = useState<any>(null);
  const [role, setRole] = useState<string>("decisor");
  const [notes, setNotes] = useState("");

  const add = trpc.crm.deals.addParticipant.useMutation({
    onSuccess: () => {
      toast.success("Participante adicionado");
      setSheetOpen(false);
      setContact(null);
      setNotes("");
      setRole("influenciador");
      utils.crm.deals.listParticipants.invalidate({ dealId });
      utils.crm.deals.listActivities.invalidate({ dealId });
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const remove = trpc.crm.deals.removeParticipant.useMutation({
    onSuccess: () => utils.crm.deals.listParticipants.invalidate({ dealId }),
  });

  const participants = listQuery.data ?? [];
  const grouped = participants.reduce((acc: any, row: any) => {
    const r = row.participant.role;
    acc[r] = acc[r] ?? [];
    acc[r].push(row);
    return acc;
  }, {});

  const orderedRoles = ["decisor", "influenciador", "tecnico", "financeiro", "usuario_final"];

  return (
    <>
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Envolvidos
            {participants.length > 0 && (
              <Badge variant="secondary">{participants.length}</Badge>
            )}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => setSheetOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Adicionar
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {participants.length === 0 && (
            <div className="text-center py-6">
              <Crown className="h-10 w-10 mx-auto mb-2 text-amber-600 opacity-50" />
              <p className="text-sm text-muted-foreground">
                B2G? Adicione o tomador de decisão aqui.
              </p>
            </div>
          )}
          {orderedRoles.map((roleKey) => {
            const rows = grouped[roleKey] ?? [];
            if (rows.length === 0) return null;
            const cfg = PARTICIPANT_ROLES[roleKey];
            const RoleIcon = cfg.icon;
            return (
              <div key={roleKey} className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <RoleIcon className="h-3.5 w-3.5" />
                  {cfg.label}
                </div>
                <div className="space-y-1.5">
                  {rows.map((row: any) => {
                    const full = [row.contact?.name, row.contact?.lastName]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <div
                        key={row.participant.id}
                        className="flex items-center gap-3 rounded-lg border px-3 py-2 bg-card hover:bg-muted/30"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs bg-primary/10 text-primary">
                            {initials(full)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{full || "-"}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {row.contact?.companyName}
                            {row.contact?.phoneNumber && ` · ${row.contact.phoneNumber}`}
                          </div>
                        </div>
                        {row.contact?.id && (
                          <Link
                            href={`/contacts/${row.contact.id}`}
                            className="text-muted-foreground hover:text-foreground"
                            title="Ver contato"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => remove.mutate({ id: row.participant.id })}
                          title="Remover"
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-[420px] w-full p-0 flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4 border-b">
            <SheetTitle>Adicionar participante</SheetTitle>
            <SheetDescription>
              Registre decisores, influenciadores e técnicos envolvidos no negócio.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            <div>
              <Label className="text-xs">Contato</Label>
              <div className="mt-1">
                <ContactSearchCombobox
                  value={contact}
                  onChange={setContact}
                  limit={20}
                  inherit={{
                    companyName: mainContact?.companyName ?? null,
                    sector: (mainContact as any)?.sector ?? null,
                    city: (mainContact as any)?.city ?? null,
                    state: (mainContact as any)?.state ?? null,
                    contactType: (mainContact?.contactType as any) ?? null,
                  }}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Papel no negócio</Label>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {Object.entries(PARTICIPANT_ROLES).map(([key, cfg]) => {
                  const active = role === key;
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setRole(key)}
                      className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm transition ${
                        active ? "border-primary bg-primary/5" : "hover:bg-muted/40"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span className="truncate">{cfg.label}</span>
                      {active && <Check className="h-3.5 w-3.5 text-primary ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs">Observação (opcional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Ex: responsável pela análise técnica da RFP"
                className="mt-1"
              />
            </div>
          </div>
          <div className="border-t px-6 py-4 flex justify-end gap-2 bg-muted/30">
            <Button variant="ghost" onClick={() => setSheetOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (!contact) return toast.error("Selecione um contato");
                add.mutate({
                  dealId,
                  contactId: contact.id,
                  role: role as any,
                  notes: notes || null,
                });
              }}
              disabled={add.isPending}
            >
              {add.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Adicionar
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// ─── Deal Tasks ──────────────────────────────────────────────────────────────
function DealTasksPanel({ dealId }: { dealId: number }) {
  const tasksQuery = trpc.crm.tasks.list.useQuery({ dealId });
  const utils = trpc.useUtils();
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const createTask = trpc.crm.tasks.create.useMutation({
    onSuccess: () => {
      setTitle("");
      setDueAt("");
      utils.crm.tasks.list.invalidate({ dealId });
      toast.success("Tarefa criada");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const complete = trpc.crm.tasks.complete.useMutation({
    onSuccess: () => utils.crm.tasks.list.invalidate({ dealId }),
  });
  const del = trpc.crm.tasks.delete.useMutation({
    onSuccess: () => utils.crm.tasks.list.invalidate({ dealId }),
  });

  const tasks = tasksQuery.data ?? [];
  const pending = tasks.filter((t: any) => t.task.status === "pending");
  const done = tasks.filter((t: any) => t.task.status === "done");

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Tarefas
          {pending.length > 0 && <Badge variant="secondary">{pending.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="Ex: Ligar para decisor..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="flex-1"
          />
          <div className="flex gap-2">
            <Input
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="flex-1 sm:flex-initial sm:w-[190px]"
            />
            <Button
              className="shrink-0"
              onClick={() => {
                if (!title.trim()) return;
                createTask.mutate({
                  title: title.trim(),
                  dealId,
                  dueAt: dueAt ? new Date(dueAt) : null,
                });
              }}
              disabled={createTask.isPending || !title.trim()}
            >
              Adicionar
            </Button>
          </div>
        </div>

        {pending.length === 0 && done.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Nenhuma tarefa ainda.</p>
          </div>
        )}

        {pending.length > 0 && (
          <div className="space-y-1.5">
            {pending.map((row: any) => {
              const isOverdue = row.task.dueAt && new Date(row.task.dueAt) < new Date();
              return (
                <div
                  key={row.task.id}
                  className={`group flex items-center gap-3 rounded-md border px-3 py-2 hover:bg-muted/30 ${
                    isOverdue ? "border-red-300 dark:border-red-900" : ""
                  }`}
                >
                  <button
                    className="h-5 w-5 rounded-full border-2 hover:border-primary hover:bg-primary/10 flex items-center justify-center shrink-0"
                    onClick={() => complete.mutate({ id: row.task.id })}
                    title="Concluir"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{row.task.title}</div>
                    {row.task.dueAt && (
                      <div
                        className={`text-xs flex items-center gap-1 mt-0.5 ${
                          isOverdue ? "text-red-600 font-medium" : "text-muted-foreground"
                        }`}
                      >
                        <Calendar className="h-3 w-3" />
                        {formatDateTime(row.task.dueAt)}
                        {isOverdue && <span>· atrasada</span>}
                      </div>
                    )}
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="opacity-0 group-hover:opacity-100"
                    onClick={() => del.mutate({ id: row.task.id })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {done.length > 0 && (
          <details className="pt-2">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
              Concluídas ({done.length})
            </summary>
            <div className="mt-2 space-y-1.5">
              {done.map((row: any) => (
                <div
                  key={row.task.id}
                  className="flex items-center gap-3 rounded-md border px-3 py-2 opacity-60"
                >
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm line-through truncate">{row.task.title}</div>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Details tab (edit) ──────────────────────────────────────────────────────
function DetailsPanel({ deal, onSave }: { deal: any; onSave: (patch: any) => void }) {
  const [title, setTitle] = useState<string>(deal.title);
  const [value, setValue] = useState<string>(String(deal.value ?? "0"));
  const [notes, setNotes] = useState<string>(deal.notes ?? "");
  const [expected, setExpected] = useState<string>(
    deal.expectedCloseDate
      ? new Date(deal.expectedCloseDate).toISOString().slice(0, 10)
      : "",
  );
  const dirty =
    title !== deal.title ||
    value !== String(deal.value ?? "0") ||
    notes !== (deal.notes ?? "") ||
    expected !==
      (deal.expectedCloseDate
        ? new Date(deal.expectedCloseDate).toISOString().slice(0, 10)
        : "");

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div>
          <Label htmlFor="edit-title" className="text-xs">
            Título
          </Label>
          <Input
            id="edit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="edit-value" className="text-xs">
              Valor (R$)
            </Label>
            <div className="relative mt-1">
              <Banknote className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                id="edit-value"
                type="number"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="pl-8 tabular-nums"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="edit-expected" className="text-xs">
              Previsão de fechamento
            </Label>
            <Input
              id="edit-expected"
              type="date"
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="edit-notes" className="text-xs">
            Notas internas
          </Label>
          <Textarea
            id="edit-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="mt-1"
          />
        </div>
        <div className="flex justify-end">
          <Button
            disabled={!dirty}
            onClick={() =>
              onSave({
                title,
                value,
                notes: notes || null,
                expectedCloseDate: expected ? new Date(expected) : null,
              })
            }
          >
            Salvar alterações
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Body ────────────────────────────────────────────────────────────────────
function DealDetailBody({ id }: { id: number }) {
  const dealQuery = trpc.crm.deals.get.useQuery({ id });
  const pipelinesQuery = trpc.crm.pipelines.list.useQuery({});
  const utils = trpc.useUtils();
  const { can } = usePermissions();
  const [, navigate] = useLocation();
  const [confirmLoss, setConfirmLoss] = useState<{ stageId: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const pipelineId = dealQuery.data?.deal.pipelineId ?? null;
  const stagesQuery = trpc.crm.stages.list.useQuery(
    pipelineId ? { pipelineId } : (undefined as any),
    { enabled: !!pipelineId },
  );

  const activitiesCount = trpc.crm.deals.listActivities.useQuery({ dealId: id }).data?.length ?? 0;
  const participantsCount =
    trpc.crm.deals.listParticipants.useQuery({ dealId: id }).data?.length ?? 0;
  const team = trpc.crm.deals.listTeam.useQuery({ dealId: id }).data ?? [];
  const teamCount = team.length;
  // Só a mais recente interessa no cabeçalho: "veio de quem, quando, por quê".
  const lastTransfer = trpc.crm.deals.listTransfers.useQuery({ dealId: id, limit: 1 }).data?.[0] ?? null;
  const podeTransferir = can("crm:deals_assign") || can("crm:deals_assign_all");
  const tasksCount =
    trpc.crm.tasks.list.useQuery({ dealId: id }).data?.filter((t: any) => t.task.status === "pending").length ?? 0;

  /**
   * O Assistente enxerga a oportunidade aberta. Sem ações de escrita aqui: o
   * que se faz nesta tela (mudar etapa, registrar atividade, editar valor) já
   * tem ferramenta própria, que passa pelo procedure com RBAC e trilha de
   * auditoria. Duplicar isso como ação de tela criaria um caminho paralelo
   * mais fraco para a mesma coisa.
   */
  useContextoDaTela(
    dealQuery.data
      ? {
          titulo: `Oportunidade: ${dealQuery.data.deal.title}`,
          dados: {
            dealId: id,
            titulo: dealQuery.data.deal.title,
            valor: dealQuery.data.deal.value,
            etapaId: dealQuery.data.deal.stageId,
            etapa: stagesQuery.data?.find((e: { id: number }) => e.id === dealQuery.data?.deal.stageId)?.name ?? null,
            funilId: pipelineId,
            contato: dealQuery.data.contact?.name ?? null,
            contatoId: dealQuery.data.deal.contactId,
            responsavel: dealQuery.data.owner?.name ?? null,
            fechamentoPrevisto: dealQuery.data.deal.expectedCloseDate,
            tarefasPendentes: tasksCount,
            atividades: activitiesCount,
          },
        }
      : null,
  );

  const changeStage = trpc.crm.deals.changeStage.useMutation({
    onSuccess: () => {
      utils.crm.deals.get.invalidate({ id });
      utils.crm.deals.listActivities.invalidate({ dealId: id });
      toast.success("Oportunidade atualizada");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const update = trpc.crm.deals.update.useMutation({
    onSuccess: () => {
      utils.crm.deals.get.invalidate({ id });
      toast.success("Salvo");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  // Edição direta dos campos do cartão — cada um salva sozinho, sem formulário.
  const stagesForEdit = (stagesQuery.data ?? []) as any[];
  const fieldEdit = useDealFieldEdit({ stages: stagesForEdit });
  const sources = useDealSources();

  const del = trpc.crm.deals.delete.useMutation({
    onSuccess: () => {
      toast.success("Oportunidade excluída");
      navigate("/crm/deals");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  if (dealQuery.isLoading) {
    return (
      <div className="space-y-4 max-w-6xl">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  if (!dealQuery.data) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <p className="font-semibold">Oportunidade não encontrada.</p>
          <Link href="/crm/deals" className="text-sm text-primary hover:underline mt-2 inline-block">
            Voltar ao funil
          </Link>
        </CardContent>
      </Card>
    );
  }

  const { deal, contact, owner } = dealQuery.data;
  const stages = stagesQuery.data ?? [];
  const stage = stages.find((s: any) => s.id === deal.stageId);
  const pipeline = pipelinesQuery.data?.find((p: any) => p.id === deal.pipelineId);
  const contactFullName = [contact?.name, contact?.lastName].filter(Boolean).join(" ");

  function handleMove(stageId: number, isLost: boolean) {
    if (isLost) {
      setConfirmLoss({ stageId });
    } else {
      changeStage.mutate({ id, toStageId: stageId });
    }
  }

  const statusBar =
    deal.status === "won"
      ? "bg-gradient-to-r from-emerald-500/20 via-emerald-500/10 to-transparent border-emerald-500/30"
      : deal.status === "lost"
      ? "bg-gradient-to-r from-red-500/20 via-red-500/10 to-transparent border-red-500/30"
      : "bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20";

  return (
    <div className="space-y-5 max-w-6xl">
      {/* ── Breadcrumb ─────────────────────────────── */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground overflow-x-auto -mx-1 px-1 whitespace-nowrap">
        <Link href="/crm/deals" className="hover:text-foreground flex items-center gap-1 shrink-0">
          <ArrowLeft className="h-3 w-3" />
          Funil
        </Link>
        <ChevronRight className="h-3 w-3 shrink-0" />
        <span className="truncate max-w-[140px]">{pipeline?.name ?? "Oportunidade"}</span>
        {stage && (
          <>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <span className="flex items-center gap-1 shrink-0">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: stage.color }}
              />
              {stage.name}
            </span>
          </>
        )}
      </div>

      {/* ── Hero card ─────────────────────────────── */}
      <Card className={`border ${statusBar}`}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0 flex-1 order-2 sm:order-1">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                {deal.status === "won" && (
                  <Badge className="bg-emerald-600">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Fechada
                  </Badge>
                )}
                {deal.status === "lost" && (
                  <Badge className="bg-red-600">
                    <XCircle className="h-3 w-3 mr-1" />
                    Perdida
                  </Badge>
                )}
                {deal.status === "open" && <Badge variant="outline">Em aberto</Badge>}
                {(() => {
                  const ref = (deal.metadata as { adReferral?: { adName?: string | null; headline?: string | null; campaignName?: string | null } } | null)?.adReferral;
                  if (!ref) return null;
                  const nome = ref.headline ?? ref.adName;
                  return (
                    <Badge
                      variant="outline"
                      className="border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200"
                      title={[nome, ref.campaignName].filter(Boolean).join(" · ") || "Veio de um anúncio"}
                    >
                      Veio do anúncio{nome ? `: ${nome}` : ""}
                    </Badge>
                  );
                })()}
                <InlineEditSelect<number | null>
                  value={deal.sourceId ?? null}
                  options={[
                    { value: null, label: "Sem origem" },
                    ...sources.options.map((s) => ({ value: s.id, label: s.name })),
                  ]}
                  onCommit={(next) =>
                    fieldEdit.commit(id, "sourceId", next, { dealTitle: deal.title })
                  }
                  canEdit={fieldEdit.canEditField("sourceId")}
                  label="origem"
                >
                  <Badge variant="secondary" className="text-[10px]">
                    {sources.label(deal.sourceId) ?? "Definir origem"}
                  </Badge>
                </InlineEditSelect>
              </div>
              <InlineEditText
                value={deal.title}
                onCommit={(next) =>
                  fieldEdit.commit(id, "title", next, { dealTitle: deal.title })
                }
                canEdit={fieldEdit.canEditField("title")}
                label="título da oportunidade"
                maxLength={300}
                inputClassName="text-xl sm:text-2xl font-bold h-auto"
              >
                <h1 className="text-xl sm:text-2xl font-bold leading-tight break-words">
                  {deal.title}
                </h1>
              </InlineEditText>
              <div className="mt-2">
                <InlineEditText
                  value={deal.description ?? null}
                  onCommit={(next) =>
                    fieldEdit.commit(id, "description", next || null, {
                      dealTitle: deal.title,
                    })
                  }
                  canEdit={fieldEdit.canEditField("description")}
                  label="descrição"
                  multiline
                  placeholder="Descreva a oportunidade..."
                >
                  <p className="text-sm text-muted-foreground break-words">
                    {deal.description || (
                      <span className="italic opacity-70">Adicionar descrição</span>
                    )}
                  </p>
                </InlineEditText>
              </div>
              {/* Quem trabalha o lead, por papel — e de quem ele veio. Fica
                  para sempre: a passagem de bastão é parte da história do deal. */}
              {(team.length > 0 || lastTransfer) && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {team.map((row: any) => (
                    <span
                      key={row.member.id}
                      className="inline-flex items-center gap-1 rounded-full border bg-background pl-0.5 pr-2 py-0.5 text-xs"
                    >
                      <DealTeamRoleBadge role={row.member.role} className="rounded-full px-1.5 py-0" />
                      <span className="font-medium truncate max-w-[140px]">
                        {row.user?.name ?? row.user?.email ?? "—"}
                      </span>
                    </span>
                  ))}
                  {lastTransfer && (
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="outline"
                            className="cursor-help gap-1 border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300"
                          >
                            <ArrowRightLeft className="h-3 w-3" />
                            Transferida
                            {lastTransfer.fromName && (
                              <span className="font-normal">de {lastTransfer.fromName}</span>
                            )}
                            <span className="font-normal opacity-70">
                              · {formatRelative(lastTransfer.transfer.createdAt)}
                            </span>
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>
                            {lastTransfer.fromName ?? "Sem responsável"} → {lastTransfer.toName ?? "—"}
                          </p>
                          {lastTransfer.transfer.reason && (
                            <p className="mt-1 opacity-80">Motivo: {lastTransfer.transfer.reason}</p>
                          )}
                          <p className="mt-1 opacity-60">
                            {formatDateTime(lastTransfer.transfer.createdAt)}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              )}
            </div>
            <div className="order-1 sm:order-2 sm:text-right">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Valor</div>
              <InlineEditCurrency
                value={deal.value}
                onCommit={(next) =>
                  fieldEdit.commit(id, "value", next, { dealTitle: deal.title })
                }
                canEdit={fieldEdit.canEditField("value")}
                align="right"
                inputClassName="text-xl font-bold"
              >
                <span className="text-2xl sm:text-3xl font-bold tabular-nums break-all">
                  {formatBRL(deal.value)}
                </span>
              </InlineEditCurrency>
              <div className="mt-1 flex sm:justify-end">
                <InlineEditDate
                  value={deal.expectedCloseDate ?? null}
                  onCommit={(next) =>
                    fieldEdit.commit(id, "expectedCloseDate", next, {
                      dealTitle: deal.title,
                    })
                  }
                  canEdit={fieldEdit.canEditField("expectedCloseDate")}
                >
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {deal.expectedCloseDate
                      ? `Prev.: ${new Date(deal.expectedCloseDate).toLocaleDateString("pt-BR")}`
                      : "Definir previsão"}
                  </span>
                </InlineEditDate>
              </div>
              {podeTransferir && deal.status === "open" && (
                <div className="mt-3 flex sm:justify-end">
                  <Button size="sm" variant="outline" onClick={() => setTransferOpen(true)}>
                    <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
                    Transferir
                  </Button>
                </div>
              )}
            </div>
          </div>

          <Separator className="my-4" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground mb-1">
                Contato principal
              </div>
              {/* O nome continua navegando para a ficha; o lápis ao lado troca
                  o contato. Transformar o nome em gatilho de edição tiraria o
                  único caminho para a ficha do contato. */}
              <InlineEditContact
                canEdit={fieldEdit.canEditField("contactId")}
                onCommit={(contactId) =>
                  fieldEdit.commit(id, "contactId", contactId, { dealTitle: deal.title })
                }
                inherit={{
                  companyName: contact?.companyName ?? null,
                  city: contact?.city ?? null,
                  state: contact?.state ?? null,
                  contactType: (contact?.contactType as never) ?? null,
                }}
              >
                {contact ? (
                  <Link
                    href={`/contacts/${contact.id}`}
                    className="flex items-center gap-2 hover:text-primary"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">
                        {initials(contactFullName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{contactFullName || "-"}</div>
                      {contact.companyName && (
                        <div className="text-xs text-muted-foreground truncate">
                          {contact.companyName}
                        </div>
                      )}
                    </div>
                  </Link>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </InlineEditContact>
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground mb-1">
                Responsável
              </div>
              {/* Trocar o responsável aqui é uma passagem de bastão como qualquer
                  outra: abre o mesmo diálogo (papel de cada um + motivo), em vez
                  do seletor solto que trocava o dono sem deixar registro legível. */}
              <button
                type="button"
                disabled={!podeTransferir}
                title={
                  podeTransferir
                    ? "Transferir para outra pessoa"
                    : "Você não tem permissão para trocar o responsável."
                }
                onClick={() => setTransferOpen(true)}
                className="group/owner -ml-1 flex w-full items-center gap-2 rounded-md px-1 py-0.5 text-left enabled:hover:bg-muted disabled:cursor-default"
              >
                {owner?.name ? (
                  <>
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">
                        {initials(owner.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{owner.name}</div>
                      {podeTransferir && (
                        <div className="text-[11px] text-muted-foreground opacity-0 group-hover/owner:opacity-100 transition-opacity">
                          Transferir…
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {podeTransferir ? "Não atribuído — atribuir" : "Não atribuído"}
                  </span>
                )}
              </button>
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground mb-1">
                Criada
              </div>
              <div className="text-sm font-medium">{formatRelative(deal.createdAt)}</div>
              <div className="text-[11px] text-muted-foreground">
                {new Date(deal.createdAt).toLocaleDateString("pt-BR")}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold tracking-wide text-muted-foreground mb-1">
                Última atualização
              </div>
              <div className="text-sm font-medium">{formatRelative(deal.updatedAt)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Pipeline tracker ──────────────────────── */}
      {stages.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <PipelineTracker
              stages={stages}
              currentStageId={deal.stageId}
              status={deal.status}
              canChange={can("crm:deals_change_stage")}
              onMove={handleMove}
            />
          </CardContent>
        </Card>
      )}

      {/* ── Tabs ──────────────────────────────────── */}
      <Tabs defaultValue="timeline">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1 overflow-x-auto -mx-1 px-1">
            <TabsList className="w-max">
              <TabsTrigger value="timeline">
                <Activity className="h-3.5 w-3.5 mr-1.5" />
                Timeline
                {activitiesCount > 0 && (
                  <Badge variant="secondary" className="ml-1.5 text-[10px] h-4">
                    {activitiesCount}
                  </Badge>
                )}
              </TabsTrigger>
              {contact?.id && (
                <TabsTrigger value="contact-history">
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  <span className="hidden sm:inline">Histórico do contato</span>
                  <span className="sm:hidden">Contato</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="participants">
                <Users className="h-3.5 w-3.5 mr-1.5" />
                <span className="hidden sm:inline">Envolvidos</span>
                <span className="sm:hidden">Pessoas</span>
                {participantsCount > 0 && (
                  <Badge variant="secondary" className="ml-1.5 text-[10px] h-4">
                    {participantsCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="team">
                <UserCog className="h-3.5 w-3.5 mr-1.5" />
                Time
                {teamCount > 0 && (
                  <Badge variant="secondary" className="ml-1.5 text-[10px] h-4">
                    {teamCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="meetings">
                <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
                Reuniões
              </TabsTrigger>
              <TabsTrigger value="tasks">
                <Clock className="h-3.5 w-3.5 mr-1.5" />
                Tarefas
                {tasksCount > 0 && (
                  <Badge variant="secondary" className="ml-1.5 text-[10px] h-4">
                    {tasksCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="products">
                <Package className="h-3.5 w-3.5 mr-1.5" />
                Produtos
              </TabsTrigger>
              {can("crm:proposals_view") && (
                <TabsTrigger value="proposals">
                  <FileSignature className="h-3.5 w-3.5 mr-1.5" />
                  Propostas
                </TabsTrigger>
              )}
              <TabsTrigger value="details">
                <FileText className="h-3.5 w-3.5 mr-1.5" />
                Detalhes
              </TabsTrigger>
            </TabsList>
          </div>
          {can("crm:deals_delete") && (
            <Button
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-destructive shrink-0"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Excluir</span>
            </Button>
          )}
        </div>
        <TabsContent value="timeline" className="pt-4">
          <ActivityTimeline dealId={id} />
        </TabsContent>
        {contact?.id && (
          <TabsContent value="contact-history" className="pt-4">
            <ContactTimeline
              contactId={contact.id}
              emptyHint="Sem eventos do contato ainda."
            />
          </TabsContent>
        )}
        <TabsContent value="participants" className="pt-4">
          <ParticipantsPanel dealId={id} />
        </TabsContent>
        <TabsContent value="team" className="pt-4">
          <DealTeamPanel dealId={id} />
        </TabsContent>
        <TabsContent value="meetings" className="pt-4">
          <MeetingsCard
            dealId={id}
            contactId={contact?.id ?? null}
            contactName={contact?.name ?? null}
          />
        </TabsContent>
        <TabsContent value="products" className="pt-4">
          <DealProductsPanel dealId={id} />
        </TabsContent>
        {can("crm:proposals_view") && (
          <TabsContent value="proposals" className="pt-4">
            <DealProposalsPanel dealId={id} />
          </TabsContent>
        )}
        <TabsContent value="tasks" className="pt-4">
          <DealTasksPanel dealId={id} />
        </TabsContent>
        <TabsContent value="details" className="pt-4">
          <DetailsPanel deal={deal} onSave={(patch) => update.mutate({ id, ...patch })} />
        </TabsContent>
      </Tabs>

      {/* ── Dialogs ─────────────────────────────── */}
      <TransferDealDialog
        dealId={id}
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
      />
      <ConfirmLossDialog
        open={!!confirmLoss}
        dealTitle={deal.title}
        onClose={() => setConfirmLoss(null)}
        onConfirm={(lossReasonId) => {
          if (!confirmLoss) return;
          changeStage.mutate({
            id,
            toStageId: confirmLoss.stageId,
            lossReasonId,
          });
          setConfirmLoss(null);
        }}
      />
      {/* Diálogo de perda do hook — usado quando a mudança de estágio parte de
          um campo editável, e não do stepper acima. */}
      {fieldEdit.lossDialog}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta oportunidade?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Todas as atividades, participantes e tarefas vinculadas também serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => del.mutate({ id })}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir oportunidade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function CrmDealDetail() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:deals_view">
        {Number.isFinite(id) ? (
          <DealDetailBody id={id} />
        ) : (
          <div className="py-12 text-center">ID inválido.</div>
        )}
      </PermissionGuard>
    </DashboardLayout>
  );
}
